export interface Project {
  idProyecto: number;
  nombre: string;
  abreviatura?: string | null;
  colorHex?: string | null;
}

export interface Category {
  idCatTareaArq: number;
  nombre?: string | null;
  abreviatura?: string | null;
  colorHex?: string | null;
  orden?: number | null;
}

export interface CreateCategoryDto {
  nombre: string;
  abreviatura?: string | null;
  colorHex?: string | null;
}

export interface UpdateCategoryDto {
  nombre?: string;
  abreviatura?: string | null;
  colorHex?: string | null;
}

// Conteo de uso devuelto por GET /api/categories/{id}/usage
// y por DELETE cuando bloquea (HTTP 409 lleva los mismos numeros).
export interface CategoryUsage {
  tareas: number;
  plantillas: number;
}

export interface Status {
  idEst: number;
  estado?: string | null;
  abreviatura?: string | null;
  colorHex?: string | null;
  orden?: number | null;
  requiresFechaCompletado: boolean;
}

export interface Priority {
  id: number;
  nombre: string;
  colorHex: string;
  weight: number;
}

export interface User {
  idCol: number;
  nombreCompleto?: string | null;
  nombre?: string | null;
  correo?: string | null;
  iniciales?: string | null;
  avatarColor?: string | null;
  departamento?: string | null;
}

export interface Caso {
  idCaso: number;
  loteCliente?: string | null;
  cliente?: string | null;
  estado?: string | null;
  idProyecto?: number | null;
  modelo?: string | null;
  idLote?: number | null;
  idBloque?: number | null;
  idModelo?: number | null;
}

export interface Block {
  idBloque: number;
  bloque?: string | null;
  idProyecto?: number | null;
}

export interface Lot {
  idLote: number;
  idBd?: string | null;
  idProyecto?: number | null;
  idBloque?: number | null;
  // Fase actual del lote en el flujo CFIA (denormalizada en Lotes.IDFaseLote).
  // Apunta a Fase.idFase. Null para lotes que todavia no fueron clasificados.
  idFaseLote?: number | null;
  // IDCol del colaborador que subio el lote a instituciones; lo muestra
  // cada tarjeta del kanban de tramites.
  idCreadorApc?: number | null;
}

// ============================================================
// Fases del CFIA + estados detallados (catalogo del Kanban de tramites)
// ============================================================
export interface Fase {
  idFase: number;
  nombre: string;
  orden: number;
  colorHex?: string | null;
}

export interface EstadoLote {
  idEstadoLote: number;
  idFase: number;
  etapa: string;
  estado: string;
  descripcion?: string | null;
  tgCaminoFeliz: boolean;
  diasProyectados?: number | null;
  orden?: number | null;
  idDependencia?: number | null;
  tipoProyecto?: string | null;
  colorHex?: string | null;
}

// DTOs de mantenimiento (T4) — el endpoint Create/Update tiene los mismos
// campos opcionales del Update salvo los que en Create son obligatorios.
export interface CreateFaseDto {
  nombre: string;
  colorHex?: string | null;
}
export interface UpdateFaseDto {
  nombre?: string;
  colorHex?: string | null;
}
export interface FaseUsage {
  lotes: number;
  estados: number;
}

export interface CreateEstadoLoteDto {
  idFase: number;
  etapa: string;
  estado: string;
  descripcion?: string | null;
  tgCaminoFeliz?: boolean;
  diasProyectados?: number | null;
  tipoProyecto?: string | null;
  colorHex?: string | null;
}
export interface UpdateEstadoLoteDto {
  idFase?: number;
  etapa?: string;
  estado?: string;
  descripcion?: string | null;
  tgCaminoFeliz?: boolean;
  diasProyectados?: number | null;
  tipoProyecto?: string | null;
  colorHex?: string | null;
}
export interface EstadoLoteUsage {
  historicos: number;
}

// Evento del timeline unificado del lote: combina cambios de estado
// (LoteEstadoHistorico) y cambios de campos auditados (LotesHistorico).
// El backend lo devuelve del mas reciente al mas viejo. El campo `tipo`
// dice que estructura tiene la fila:
//   - "estado" -> usa idEstadoLote* + etapa* + diasEnEstadoAnterior
//   - "campo"  -> usa columna + valorAnterior + valorNuevo
// Modelo del catalogo. Lo carga el dropdown del editor de Modelo Tramitado
// en el modal del lote. El backend filtra por Activo + flag por-proyecto
// (ActivoVN / ActivoVI / ActivoVB / ActivoVC).
export interface Modelo {
  idModelo: number;
  nombre?: string | null;
}

// Modelo con croquis, para el dropdown del editor de plantas
// (GET /api/planta/modelos). No trae flags por proyecto porque ese dropdown no
// elige un modelo PARA un lote: busca una lamina para medir.
//
// `croquis` es el NOMBRE DE ARCHIVO del PDF, no su URL. Sirve para dos cosas:
// buscar la planta ya medida en el catalogo local de `planta/medidos/` (sus
// llaves son justo esos nombres) y rotular de donde salio. La URL del blob no
// viaja porque el navegador no la puede bajar: `expcliente` tiene lista blanca
// de CORS y `arquiflow.adelante.cr` no esta en ella.
//
// Se llama `ModeloConCroquis` y no `ModeloPlanta` porque ese nombre ya es el de
// la GEOMETRIA editable en `components/planta/tipos.ts`, y las dos cosas se
// cruzan en la misma pantalla: esto es la fila del dropdown, eso es la casa.
export interface ModeloConCroquis {
  idModelo: number;
  nombre?: string | null;
  croquis?: string | null;
}

// Modelo en el payload de /api/catalogs: incluye los flags de actividad por
// proyecto para filtrar el dropdown segun la abreviatura del proyecto elegido.
export interface CatalogModelo {
  idModelo: number;
  nombre?: string | null;
  activoVN: boolean;
  activoVI: boolean;
  activoVB: boolean;
  activoVC: boolean;
}

export type LoteHistoricoTipo = 'estado' | 'campo';

export interface LoteHistoricoEvento {
  tipo: LoteHistoricoTipo;
  fechaCambio: string;
  idModificadopor?: number | null;
  nomModificador?: string | null;

  // Solo si tipo === 'estado':
  idEstadoLoteAnterior?: number | null;
  etapaAnterior?: string | null;
  estadoAnterior?: string | null;
  descripcionEstadoAnterior?: string | null;
  idEstadoLoteNuevo?: number | null;
  etapaNuevo?: string | null;
  estadoNuevo?: string | null;
  descripcionEstadoNuevo?: string | null;
  colorEstadoNuevo?: string | null;
  idFaseNueva?: number | null;
  nomFaseNueva?: string | null;
  diasEnEstadoAnterior?: number | null;

  // Solo si tipo === 'campo':
  columna?: string | null;
  valorAnterior?: string | null;
  valorNuevo?: string | null;
}

// Tarjeta del Kanban de tramites CFIA. Combina datos de Lotes (idFaseLote),
// V_LotesSVG (modelo/cliente del caso vigente) y la ultima entrada del
// historico de estados (estado actual + dias en ese estado).
export interface LoteKanban {
  idLote: number;
  idBd?: string | null;
  lote?: string | null;
  idProyecto?: number | null;
  proyecto?: string | null;
  idBloque?: number | null;
  nomBloque?: string | null;

  // ID del tramite vigente del lote (modelo B0). null si el lote no tiene
  // tramite todavia (Sin Tramitar). Aun no se consume en la UI.
  idTramite?: number | null;

  // Columna del kanban. Si null, la UI agrupa en "Sin Tramitar".
  idFaseLote?: number | null;
  nomFase?: string | null;

  // Estado especifico actual (de la ultima fila del historico). En T2 viene
  // todo null porque el historico todavia no se popula.
  idEstadoLoteActual?: number | null;
  etapaActual?: string | null;
  estadoActual?: string | null;
  descripcionEstadoActual?: string | null;
  colorEstadoActual?: string | null;
  tipoProyectoActual?: string | null;
  tgCaminoFelizActual?: boolean | null;
  diasProyectados?: number | null;
  diasEnEstado?: number | null;
  fechaCambioActual?: string | null;

  idCreadorApc?: number | null;
  nomCreadorApc?: string | null;

  // Modelos: tramitado (jerarquia primaria, IDModDefecto) y reservado
  // (caso vigente). La UI muestra ambos cuando difieren. idModDefecto se
  // expone para que el editor inline pueda pre-seleccionar el valor.
  idModDefecto?: number | null;
  modeloTramitado?: string | null;
  modeloReservado?: string | null;

  // Datos del caso vigente (vienen de V_LotesSVG).
  cliente?: string | null;
  niveles?: string | null;
  banco?: string | null;
  vendedor?: string | null;
  formalizador?: string | null;
  fechaReserva?: string | null;

  // Codigos del tramite CFIA / municipalidad. Editables inline en el modal
  // del lote. Strings libres (NVARCHAR(50) en SQL).
  codigoApc?: string | null;
  permisoConstruccion?: string | null;
}

// ============================================================
// Bootstrap CFIA (B3): bandeja "Por vincular". El backend agrupa los
// CorreosCFIA en estado PorVincular por codigo (CFIA, o APC si no hay CFIA)
// y, por grupo, sugiere los 0-3 lotes candidatos del motor. El humano vincula
// el TRAMITE -> lote una sola vez; despues los correos del mismo codigo
// amarran solos. Ver docs/bootstrap-cfia-diseno.md seccion 9.
// ============================================================

// Confianza del candidato (string desde el backend; JsonStringEnumConverter):
//   - Alta              : proyecto+lote exacto, o bloque-letra unico (K.19).
//   - AltaPeroVerificar : numerico unico sin proyecto -> puede ser una fecha
//                         que matchea por coincidencia (12.22), revisar.
//   - Media             : 2-3 candidatos ambiguos, el humano elige.
export type Confianza = 'Alta' | 'AltaPeroVerificar' | 'Media';

// Un lote candidato para un grupo. tieneTramiteVigente dispara el camino
// re-tramite (9.6): vincular sobre un lote que YA tiene tramite vigente.
export interface LoteCandidato {
  idLote: number;
  idBd?: string | null;
  proyecto?: string | null;
  // Dos modelos (resueltos server-side, id+nombre): TRAMITADO = IDModDefecto
  // (lo que esta en proceso ante el CFIA); RESERVADO = modelo del caso vigente
  // (lo que firmo el cliente), null si el lote no tiene caso. La bandeja muestra
  // ambos y el operador elige cual prefilear (default = reservado-si-existe).
  idModeloTramitado?: number | null;
  modeloTramitado?: string | null;
  idModeloReservado?: number | null;
  modeloReservado?: string | null;
  confianza: Confianza;
  tieneTramiteVigente: boolean;
}

// Grupo de correos = un tramite (mismo codigo). La accion de vincular opera
// sobre el grupo entero, nunca correos sueltos (evita vinculacion parcial).
export interface BootstrapGrupo {
  codigo: string;
  tipoCodigo: 'CFIA' | 'APC' | 'NINGUNO';
  cantidadCorreos: number;
  tipos: string[];
  fechaPrimera?: string | null;
  fechaUltima?: string | null;
  textosLoteRaw: string[];
  notas: string[];
  idsCorreo: number[];
  candidatos: LoteCandidato[];
  // Algun candidato ya tiene tramite vigente -> ojo, posible re-tramite (9.6).
  posibleReTramite: boolean;
}

// Un LOTE de la bandeja con TODOS sus codigos juntos. Es la unidad de decision
// real: medido el 26-oct-2026, los 199 codigos con candidato unico apuntan a
// solo 86 lotes (hay lotes con 2, 3, 4 y hasta 5 codigos). Como el indice
// UX_TramiteAD_Vigente deja UN vigente por lote, mostrarlos sueltos hacia que
// el humano vinculara el primero y los otros reventaran con 409 uno por uno.
export interface BandejaLote {
  idLote: number;
  idBd?: string | null;
  proyecto?: string | null;
  idModeloTramitado?: number | null;
  modeloTramitado?: string | null;
  idModeloReservado?: number | null;
  modeloReservado?: string | null;
  tieneTramiteVigente: boolean;
  // La PEOR confianza de sus codigos: si uno es "verificar", el lote lo es.
  confianza: Confianza;
  cantidadCodigos: number;
  cantidadCorreos: number;
  fechaPrimera?: string | null;
  fechaUltima?: string | null;
  // Estado que quedaria si se vinculan todos sus codigos juntos. Sale del
  // tramite VIGENTE = el CFIA mas alto + el APC mas alto (el consecutivo mas
  // alto es el re-tramite mas reciente), NO del correo mas avanzado del lote.
  tipoMasAvanzado?: string | null;
  idEstadoSugerido?: number | null;
  estadoSugerido?: string | null;
  etapaSugerida?: string | null;
  // Los codigos que mandan (1 o 2). El resto son procedencia del re-tramite.
  codigosVigentes: string[];
  // Hasta donde llego el tramite ANTERIOR. Solo viene cuando un codigo viejo
  // quedo mas avanzado que el vigente: el lote se volvio a tramitar (ventas) y
  // la etapa que vale es la del codigo nuevo, aunque sea "para atras".
  estadoPrevio?: string | null;
  tipoMasAvanzadoPrevio?: string | null;
  // Planos del lote (V_LotesSVG) para comprobar sin salir de la tarjeta. NULL =
  // no cargado -> boton deshabilitado. El croquis de RESERVA no esta: vive en el
  // expediente del caso (tblAdjuntos, IDReq=64).
  linkCatastro?: string | null;
  linkTramitado?: string | null;
  linkConstruido?: string | null;
  grupos: BootstrapGrupo[];
}

// Respuesta de GET /api/bootstrap/por-vincular: tres cubos. `lotes` es el
// trabajo real (candidato unico, agrupado por lote). `ambiguos` y
// `sinSugerencia` siguen por codigo porque su lote NO esta decidido todavia, y
// los dos SIEMPRE se muestran (esconderlos = no se procesan = la bandeja no se
// vacia).
export interface PorVincular {
  lotes: BandejaLote[];
  ambiguos: BootstrapGrupo[];
  sinSugerencia: BootstrapGrupo[];
}

// Body de POST /api/bootstrap/vincular. Codigos + estado inicial NO viajan
// aca: el backend los deriva del grupo (idsCorreo). idModelo/idCliente son
// opcionales (vinculacion por partes, 9.3); en el v1 de la UI solo se manda
// idModelo (cliente queda fuera del v1).
export interface VincularRequest {
  idsCorreo: number[];
  idLote: number;
  idModelo?: number | null;
  idCliente?: number | null;
  // Copia idModelo al «Modelo Tramitado» del lote (Lotes.IDModDefecto) ademas
  // de guardarlo en el tramite. Son campos distintos a proposito -> opt-in.
  aplicarModeloAlLote?: boolean;
}

// Respuesta OK del vincular.
export interface VincularResult {
  idTramite: number;
  idLote: number;
  idBd?: string | null;
  idEstadoLoteInicial: number;
  correosVinculados: number;
}

// Datos del tramite VIEJO que el 409 del vincular devuelve cuando el lote ya
// tiene uno vigente (9.6). El PR 1 solo muestra el mensaje; el dialogo de
// re-tramite (que usa estos datos para decidir el camino) es del PR 2.
export interface TramiteVigenteInfo {
  idTramite: number;
  codigoCfia?: string | null;
  codigoApc?: string | null;
  idModelo?: number | null;
  modeloActual?: string | null;
  idCliente?: number | null;
  idEstadoLoteVigente?: number | null;
  estadoVigente?: string | null;
  faseVigente?: string | null;
  fechaEstadoVigente?: string | null;
}

// Cuerpo del 409 "re-tramite requerido". Mensaje legible que NO asume cliente
// nuevo (puede ser re-tramite real O el mismo tramite con su otro codigo).
export interface ReTramiteRequerido {
  mensaje: string;
  tramiteVigente: TramiteVigenteInfo;
}

// Resultado del buscador manual (endpoint 2, escape de 9.4). Sin confianza
// (busqueda directa, no candidato rankeado). idModelo para prefilear el combo
// igual que el candidato.
export interface LoteBusqueda {
  idLote: number;
  idBd?: string | null;
  proyecto?: string | null;
  idModeloTramitado?: number | null;
  modeloTramitado?: string | null;
  idModeloReservado?: number | null;
  modeloReservado?: string | null;
  tieneTramiteVigente: boolean;
}

// Body de POST /api/bootstrap/re-tramite (endpoint 4, camino EXPLICITO 9.6).
// Solo se llama tras el 409 del vincular, con el camino que el humano eligio
// viendo el tramite viejo. idModelo/idCliente solo aplican a 'nuevo' ('mismo'
// no crea nada). Cliente fuera del v1 de la UI.
export interface ReTramiteRequest {
  idsCorreo: number[];
  idLote: number;
  camino: 'nuevo' | 'mismo';
  idModelo?: number | null;
  idCliente?: number | null;
  // Igual que en VincularRequest. En el camino 'mismo' el modelo va al tramite
  // existente solo si estaba vacio, pero el lote si se escribe si el check esta.
  aplicarModeloAlLote?: boolean;
}

// Respuesta OK del re-tramite. idTramite = el resultante (nuevo en 'nuevo', el
// vigente en 'mismo'). idTramiteJubilado = el viejo (solo en 'nuevo').
export interface ReTramiteResult {
  camino: string;
  idTramite: number;
  idLote: number;
  idBd?: string | null;
  idEstadoLoteInicial?: number | null;
  correosVinculados: number;
  idTramiteJubilado?: number | null;
}

// ============================================================
// B4: auto-vinculo con salvaguarda + cola de verificacion (9.0 / 9.5 / 9.6).
// El auto-vinculo resuelve los grupos donde no hay juicio humano que agregar
// (un solo candidato, confianza Alta, codigo presente, sin re-tramite) y los
// deja marcados para una pasada de verificacion, que es LEER, no decidir.
// ============================================================

// dryRun = contar sin escribir (el paso previo obligatorio en produccion).
// limite > 0 = procesar solo los primeros N (corrida de prueba chica).
export interface AutoVincularRequest {
  dryRun?: boolean;
  limite?: number;
}

// omitidosPorMotivo es el desglose de lo que QUEDA para el humano: la suma de
// sus valores son las decisiones reales de la maraton.
export interface AutoVincularResult {
  dryRun: boolean;
  gruposTotales: number;
  elegibles: number;
  procesados: number;
  vinculados: number;
  fallidos: number;
  omitidosPorMotivo: Record<string, number>;
  errores: string[];
}

// Una fila de la cola de verificacion: lo que el parser LEYO (textosLoteRaw)
// contra el lote que el motor ELIGIO (idBd + identificador).
export interface PorVerificar {
  idTramite: number;
  idLote: number;
  idBd?: string | null;
  identificador?: string | null;
  proyecto?: string | null;
  codigoCfia?: string | null;
  codigoApc?: string | null;
  idModelo?: number | null;
  modelo?: string | null;
  idEstadoLoteVigente?: number | null;
  estadoVigente?: string | null;
  // Cuando corrio el auto-vinculo (identica en todas las filas de una corrida).
  fechaCreacion: string;
  // Fecha del correo mas reciente del tramite: el dato que el operador puede
  // juzgar de verdad.
  fechaUltimoCorreo?: string | null;
  cantidadCorreos: number;
  textosLoteRaw: string[];
}

// 'confirmar' firma el tramite (sale de la cola); 'rechazar' lo jubila y
// devuelve sus correos a la bandeja para vincularlos al lote correcto.
export interface VerificarRequest {
  idTramite: number;
  accion: 'confirmar' | 'rechazar';
}

export interface VerificarResult {
  accion: string;
  idTramite: number;
  idLote: number;
  idBd?: string | null;
  correosDevueltos: number;
}

// Sprints: tabla SprintsARQ (script 013). 'SinAsignar' es el sprint cero
// (unico, no editable, recibe las tareas historicas via backfill).
// 'Semanal' lo crea el script / la Timer Function lun-dom. 'Especial' lo
// crea el usuario a mano. fechaInicio/fechaFin son null solo en SinAsignar.
export type SprintTipo = 'SinAsignar' | 'Semanal' | 'Especial';

export interface Sprint {
  idSprint: number;
  nombre: string;
  tipo: SprintTipo;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  activo: boolean;
}

export interface CreateSprintDto {
  nombre: string;
  fechaInicio: string;
  fechaFin: string;
}

export interface UpdateSprintDto {
  nombre?: string;
  fechaInicio?: string;
  fechaFin?: string;
  activo?: boolean;
}

// ============================================================
// Capacidad de horas por colaborador (F3 del feature de sprints).
// ============================================================

// Default por usuario (tabla ColaboradorCapacidadARQ).
export interface CapacidadDefault {
  idCol: number;
  horasDefault: number;
  /** Horas por sprint reservadas para revisión/autorización. */
  horasRevision: number;
}

// Override por sprint+colaborador. Si existe pisa al default solo para ese
// sprint. Para limpiar el override y volver al default, DELETE el endpoint.
export interface CapacidadOverride {
  idSprint: number;
  idCol: number;
  horasOverride: number;
}

// Capacidad efectiva para un colaborador en un sprint dado: combina default,
// override (si lo hay) y horas ya asignadas (suma de HorasEstimadas de
// tareas activas del sprint con ese responsable, sin filtrar por completed).
// Lo usa el indicador del kanban /assignee y las tarjetas del dashboard.
export interface EffectiveCapacidad {
  idCol: number;
  nombreCompleto?: string | null;
  horasEfectivas: number;       // bruta (default u override)
  tieneOverride: boolean;
  horasAsignadas: number;
  horasRevision: number;        // reserva para revisión
  horasDisponibles: number;     // = max(0, efectivas - revisión)
}

// ============================================================
// Preferencias de notificaciones de Teams (self-service por usuario).
// "Mías" (por rol) default true; "observador" (todas) default false.
// ============================================================
export interface NotifPref {
  notifAsignada: boolean;
  notifAsignadaRevisor: boolean;
  notifRevisionRevisor: boolean;
  notifMiCompletada: boolean;
  notifMiCorreccion: boolean;
  notifMiEnProceso: boolean;
  notifMiEliminada: boolean;
  notifTodasCreadas: boolean;
  notifTodasEnProceso: boolean;
  notifTodasEliminadas: boolean;
  notifRecordatorioDiario: boolean;
}

// ============================================================
// Timeline + Feed (audit log): eventos unificados de comentarios
// (BitacoraARQ) + cambios + creacion (TareasHistorico).
// ============================================================
export type TimelineEventoTipo = 'comentario' | 'cambio' | 'creacion';

// Un cambio puntual. El frontend resuelve `columna` y los valores via
// catalogos (estados/users/sprints/categorias/proyectos/prioridades).
export interface CambioRaw {
  columna: string;
  valorAnterior: string | null;
  valorNuevo: string | null;
}

export interface TimelineEvento {
  tipo: TimelineEventoTipo;
  fecha: string;
  idActor: number | null;
  nomActor: string | null;
  idBitacora?: number | null;
  comentario?: string | null;
  cambios?: CambioRaw[] | null;
}

export interface FeedItem {
  tipo: TimelineEventoTipo;
  fecha: string;
  idActor: number | null;
  nomActor: string | null;
  idTareaArq: number;
  nombreTarea: string | null;
  idBitacora?: number | null;
  comentario?: string | null;
  cambios?: CambioRaw[] | null;
}

export interface FeedPage {
  items: FeedItem[];
  offset: number;
  limit: number;
  hasMore: boolean;
}

// ============================================================
// Dashboard (F4): tarjeta por colaborador para un sprint dado.
// ============================================================

// Item por colaborador. 3 buckets de tareas disjuntos (PorTrabajar /
// Completadas / SinAutorizar) + heuristica de Arrastradas (FechaCreacion
// previa al lunes del sprint) + capacidad y horas asignadas.
export interface DashboardItem {
  idCol: number;
  nombreCompleto?: string | null;
  porTrabajar: number;
  completadas: number;
  sinAutorizar: number;   // tareas propias en Revisión (esperan que su autorizador revise)
  comoRevisor: number;    // total de tareas del sprint donde es autorizador (cualquier estado)
  porRevisar: number;     // subset de comoRevisor en Revisión (pendientes de que él revise)
  arrastradas: number;
  horasAsignadas: number;
  horasEfectivas: number;
  tieneOverride: boolean;
  horasRevision: number;        // reserva para revisión
  horasDisponibles: number;     // = max(0, efectivas - revisión)
}

export interface Dashboard {
  idSprint: number;
  nombreSprint: string;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  items: DashboardItem[];
}

// ============================================================
// Poker Planning (F5 del feature de sprints).
// ============================================================

export type PokerEstado = 'Abierta' | 'Cerrada';

// Voto individual. Mientras la votacion esta Abierta y NO es mi propio
// voto, el backend manda Valor=null + EsInterrogante=false + Oculto=true
// (se ve "ya voto" pero no que voto). Al cerrar se revelan todos.
export interface PokerVoto {
  idCol: number;
  nombreCompleto?: string | null;
  valor: number | null;
  esInterrogante: boolean;
  fechaVoto: string;
  oculto: boolean;
}

export interface PokerVotacion {
  idVotacion: number;
  idTarea: number;
  estado: PokerEstado;
  escalaValores: number[];
  valorFinal?: number | null;
  idIniciador: number;
  nomIniciador?: string | null;
  fechaApertura: string;
  fechaCierre?: string | null;
  idCerradoPor?: number | null;
  nomCerradoPor?: string | null;
  votos: PokerVoto[];
}

export interface Catalogs {
  projects: Project[];
  categories: Category[];
  statuses: Status[];
  priorities: Priority[];
  users: User[];
  cases: Caso[];
  blocks: Block[];
  lots: Lot[];
  sprints: Sprint[];
  modelos: CatalogModelo[];
}

export interface Task {
  idTareaArq: number;
  nombreTarea?: string | null;
  // Parte personalizada del nombre concatenado (null = legacy / nombre libre).
  nombreCustom?: string | null;
  descripcion?: string | null;
  observacion?: string | null;

  idProyecto?: number | null;
  nomProyecto?: string | null;
  colorProyecto?: string | null;

  idCategoria?: number | null;
  nomCategoria?: string | null;
  colorCategoria?: string | null;

  idEstado?: number | null;
  nomEstado?: string | null;
  colorEstado?: string | null;
  ordenEstado?: number | null;

  prioridad?: number | null;
  nomPrioridad?: string | null;
  colorPrioridad?: string | null;

  responsable?: number | null;
  nomResponsable?: string | null;

  creadopor?: number | null;
  nomCreador?: string | null;
  nomModificador?: string | null;
  nomAutorizado?: string | null;

  idCaso?: number | null;
  idSolicitud?: number | null;
  idSolicitante?: number | null;
  idLote?: number | null;
  idBloque?: number | null;
  idModelo?: number | null;

  fechaInicio?: string | null;
  fechaProyectada?: string | null;
  // Inicio planeado (par de fechaProyectada = fin planeado). El Gantt dibuja la
  // barra desde aca cuando la tarea todavia no arranco (no tiene fechaInicio).
  fechaProyectadaIn?: string | null;
  fechaCompletado?: string | null;
  fechaAutorizadoInt?: string | null;
  fechaSolicitud?: string | null;
  fechaCreacion?: string | null;
  fechaModificacion?: string | null;

  porAvance?: number | null;
  linkEntrega?: string | null;
  pesoTarea?: number | null;
  diasIp?: number | null;
  diasIe?: number | null;

  // Jerarquia de tareas: padre de esta tarea (null = raiz). El Gantt arma el
  // arbol con esto; el TaskModal lo edita.
  idTareaPadre?: number | null;

  positionByStatus: number;
  positionByAssignee: number;
  positionByProject: number;
  rowVersion?: string | null;

commentCount: number;
  checklistTotal: number;
  checklistDone: number;

  tgIniciado?: boolean | null;
  tgCompletado?: boolean | null;
  tgAutorizadoInt?: boolean | null;
  idAutorizadoInt?: number | null;

  // Soft-delete. Nuevas tareas se crean con true; el FilterPanel oculta las
  // inactivas por defecto (toggle Activas/Inactivas/Todas en UI).
  esActivo: boolean;

  // Sprint asignado. null mientras la tarea no recibe el backfill / default
  // del backend; el frontend resuelve el nombre via catalogs.sprints.
  idSprint?: number | null;
  // Estimacion en horas. La llena el usuario o el resultado del poker (F5).
  horasEstimadas?: number | null;
}

export interface CreateTaskDto {
  nombreTarea: string;
  // Parte personalizada del nombre concatenado (null = nombre libre).
  nombreCustom?: string | null;
  descripcion?: string | null;
  observacion?: string | null;
  idProyecto?: number | null;
  idCategoria?: number | null;
  idEstado?: number | null;
  prioridad?: number | null;
  responsable?: number | null;
  idCaso?: number | null;
  idLote?: number | null;
  idBloque?: number | null;
  idModelo?: number | null;
  idSolicitud?: number | null;
  idSolicitante?: number | null;
  fechaInicio?: string | null;
  fechaProyectada?: string | null;
  fechaProyectadaIn?: string | null;
  fechaCompletado?: string | null;
  fechaAutorizadoInt?: string | null;
  idAutorizadoInt?: number | null;
  linkEntrega?: string | null;
  pesoTarea?: number | null;
  idTareaPadre?: number | null;
  // Opcional: si se omite, el backend la crea con true.
  esActivo?: boolean;
  // Opcional: si se omite el backend resuelve el sprint actual (semanal vigente
  // o el sprint cero "Sin Asignar" como fallback).
  idSprint?: number | null;
  horasEstimadas?: number | null;
}

export interface UpdateTaskDto extends Partial<CreateTaskDto> {
  porAvance?: number | null;
}

export interface MoveTaskDto {
  groupBy: 'status' | 'assignee' | 'project';
  targetGroupId?: number | null;
  beforePosition?: number | null;
  afterPosition?: number | null;
  rowVersion?: string;
}

// Dependencia (vínculo) entre tareas para el Gantt. Nombres alineados a ILink
// de SVAR (source = predecesora, target = sucesora, type = e2s/s2s/e2e/s2e).
export interface Dependencia {
  id: number;
  source: number;
  target: number;
  type: string;
  lag?: number | null;
}

export interface CreateDependenciaDto {
  source: number;
  target: number;
  type?: string;
  lag?: number | null;
}

export interface BitacoraEntry {
  idBitacoraArq: number;
  idTareaArq: number;
  idCreadopor?: number | null;
  nomCreador?: string | null;
  comentario?: string | null;
  categoria?: string | null;
  tipoComentario?: string | null;
  fechaCreacion?: string | null;
  fechaModificacion?: string | null;
}

export type ChecklistTipo = 'checklist' | 'revision';

// Tipo en una plantilla. Suma 'ambas' para plantillas reutilizables en los
// dos contextos (Checklist y Revision) sin duplicar. Los items siempre se
// instancian con un ChecklistTipo concreto — la plantilla 'ambas' solo es
// un marcador de disponibilidad; al cargarla el destino se define por el
// tab desde donde se aprieta "Cargar plantilla".
export type PlantillaTipo = ChecklistTipo | 'ambas';

export interface ChecklistEntry {
  idChecklistArq: number;
  idTareaArq: number;
  texto?: string | null;
  descripcion?: string | null;
  completado?: boolean | null;
  autorizado?: boolean | null;
  idCategoria?: number | null;
  fechaCreacion?: string | null;
  fechaCompletado?: string | null;
  tipo: ChecklistTipo;
}

// ============================================================
// Plantillas de Checklist (cabecera + items)
// ============================================================
export interface PlantillaTareaSummary {
  idPlantillaTarea: number;
  nombre: string;
  idCategoria: number;
  tipo: PlantillaTipo;
  activo: boolean;
  esPredeterminada: boolean;
  itemCount: number;
}

export interface PlantillaTareaItem {
  idPlantillaTareaItem: number;
  idPlantillaTarea: number;
  texto: string;
  descripcion?: string | null;
  orden: number;
}

export interface PlantillaTarea {
  idPlantillaTarea: number;
  nombre: string;
  idCategoria: number;
  tipo: PlantillaTipo;
  activo: boolean;
  esPredeterminada: boolean;
  fechaCreacion?: string | null;
  idCreadopor?: number | null;
  items: PlantillaTareaItem[];
}

export interface CreatePlantillaTareaDto {
  nombre: string;
  idCategoria: number;
  tipo: PlantillaTipo;
  esPredeterminada?: boolean;
}

export interface UpdatePlantillaTareaDto {
  nombre?: string;
  idCategoria?: number;
  tipo?: PlantillaTipo;
  activo?: boolean;
  esPredeterminada?: boolean;
}

export interface CreatePlantillaTareaItemDto {
  texto: string;
  descripcion?: string | null;
  orden?: number;
}

export interface UpdatePlantillaTareaItemDto {
  texto?: string;
  descripcion?: string | null;
  orden?: number;
}

export type GroupBy = 'status' | 'assignee' | 'project';

export type DateField =
  | 'fechaInicio'
  | 'fechaProyectada'
  | 'fechaCompletado'
  | 'fechaAutorizadoInt'
  | 'fechaCreacion';

export const DATE_FIELD_LABELS: Record<DateField, string> = {
  fechaInicio: 'Fecha Inicio',
  fechaProyectada: 'Fecha Proyectada',
  fechaCompletado: 'Fecha Completado',
  fechaAutorizadoInt: 'Fecha Autorizado Int.',
  fechaCreacion: 'Fecha Creación',
};

export type AuthFilterValue = 'all' | 'authorized' | 'unauthorized';

// Filtro por soft-delete: 'active' (default) oculta inactivas, 'inactive'
// muestra solo inactivas, 'all' no aplica el filtro.
export type ActivoFilterValue = 'active' | 'inactive' | 'all';

export interface ActiveFilters {
  estadoId: number | null;
  proyectoId: number | null;
  categoriaId: number | null;
  asignadoId: number | null | 'unassigned';
  // Filtro de estado de autorización: 'all' = no aplica filtro,
  // 'authorized' = solo tgAutorizadoInt true, 'unauthorized' = solo false.
  authStatus: AuthFilterValue;
  autorizadorId: number | null | 'unassigned';
  // Soft-delete: default 'active' oculta las archivadas. La UI lo expone como
  // SegmentedControl Activas / Inactivas / Todas.
  activoStatus: ActivoFilterValue;
  // Sprint: null = no aplica filtro, number = solo tareas de ese sprint.
  // El sprint visible por default lo elige el TopBar (selectedSprintId del store);
  // este filtro es para overridear desde el panel lateral.
  sprintId: number | null;
  dateField: DateField;       // siempre tiene valor; default = fechaInicio
  filterYear: number | null;  // null = cualquier año (filtro inactivo)
  filterMonth: number | null; // 1-12, null = cualquier mes; solo aplica si filterYear != null
}

export const emptyFilters: ActiveFilters = {
  estadoId: null,
  proyectoId: null,
  categoriaId: null,
  asignadoId: null,
  authStatus: 'all',
  autorizadorId: null,
  activoStatus: 'active',
  sprintId: null,
  dateField: 'fechaInicio',
  filterYear: null,
  filterMonth: null,
};